/**
 * API Keys & Tokens — sk-notification's own keys (external callers → POST
 * /notify). One-time plaintext reveal, issue form, keys table, revoke.
 * Ported from the console. Auth = the console's platform JWT (Bearer).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TextInput, StyleSheet } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import { useToast } from '../components/toast';
import Icon from '../components/notif/Icon';
import { Copyable } from '../components/notif/badges';
import { PageHeader, EmptyState, Btn, Banner } from '../components/notif/ui';
import { listApiKeys, issueApiKey, revokeApiKey, ApiKeyRecord } from '../services/notification/apiKeys';
import { getAllUsage } from '../services/notification/usage';
import { relTime } from '../utils/formatters';
import { handleApiError } from '../utils/errorHandler';
import { useT } from '../i18n/useT';

export default function ApiKeysScreen() {
  const { theme: { colors, font } } = useTheme();
  const { pad } = useResponsive();
  const toast = useToast();
  const t = useT();

  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [products, setProducts] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [productId, setProductId] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [justIssued, setJustIssued] = useState<{ plaintext: string; name: string } | null>(null);

  const load = () => {
    setLoading(true); setErr(false);
    listApiKeys()
      .then(setKeys)
      .catch(e => { setErr(true); handleApiError(e, t('apiKeys.loadError')); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { getAllUsage().then(d => setProducts((d.products ?? []).map(p => p.productId))).catch(() => {}); }, []);

  const canIssue = !!name.trim() && !!productId.trim() && !issuing;

  const issue = async () => {
    if (!canIssue) return;
    setIssuing(true);
    try {
      const res = await issueApiKey({ name: name.trim(), productId: productId.trim() });
      setJustIssued({ plaintext: res.plaintext, name: res.key.name });
      setName(''); setProductId('');
      toast.success(t('apiKeys.toastIssuedTitle'), { message: t('apiKeys.toastIssuedBody') });
      load();
    } catch (e) {
      handleApiError(e, t('apiKeys.errIssue'));
    } finally {
      setIssuing(false);
    }
  };

  const revoke = (key: ApiKeyRecord) => {
    revokeApiKey(key.id)
      .then(() => { toast.success(t('apiKeys.toastRevokedTitle'), { message: key.name }); load(); })
      .catch(() => toast.error(t('apiKeys.errRevoke'), { message: key.name }));
  };

  const ordered = useMemo(() => [...keys.filter(x => !x.revokedAt), ...keys.filter(x => x.revokedAt)], [keys]);

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 16 }}>
      <PageHeader
        title={t('apiKeys.title')}
        subtitle={t('apiKeys.subtitle')}
        right={<Btn label={t('common.refresh')} icon="refresh" size="sm" onPress={load} />}
      />

      {justIssued && (
        <Banner tone="success" onClose={() => setJustIssued(null)}>
          <Text style={{ color: colors.ink, fontWeight: '600', fontSize: 13 }}>{t('apiKeys.justIssuedTitle', { name: justIssued.name })}</Text>
          <Text style={{ color: colors.ink3, fontSize: 12, marginTop: 2, marginBottom: 8 }}>{t('apiKeys.justIssuedBody')}</Text>
          <Copyable value={justIssued.plaintext} />
        </Banner>
      )}

      <View style={[k.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
        <Text style={{ color: colors.ink, fontSize: 13, fontWeight: '600', marginBottom: 12 }}>{t('apiKeys.issueSection')}</Text>
        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <View style={{ flex: 1, minWidth: 180 }}>
            <Text style={[k.label, { color: colors.ink3 }]}>{t('apiKeys.nameLabel')}</Text>
            <TextInput value={name} onChangeText={setName} placeholder={t('apiKeys.namePlaceholder')} placeholderTextColor={colors.ink4} style={[k.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
          </View>
          <View style={{ flex: 1, minWidth: 180 }}>
            <Text style={[k.label, { color: colors.ink3 }]}>{t('apiKeys.productLabel')}</Text>
            <TextInput value={productId} onChangeText={setProductId} placeholder={t('apiKeys.productPlaceholder')} placeholderTextColor={colors.ink4} style={[k.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
            {products.length > 0 && (
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                {products.slice(0, 8).map(p => (
                  <Text key={p} onPress={() => setProductId(p)} style={{ color: colors.accent, fontSize: 11, fontFamily: font.mono }}>{p}</Text>
                ))}
              </View>
            )}
          </View>
          <Btn label={issuing ? t('apiKeys.issuing') : t('apiKeys.issueKey')} kind="primary" disabled={!canIssue} onPress={issue} />
        </View>
      </View>

      {loading && <EmptyState title={t('apiKeys.loading')} />}
      {err && !loading && <EmptyState icon="alert" title={t('apiKeys.loadError')} action={<Btn label={t('common.retry')} onPress={load} />} />}
      {!loading && !err && (
        <View style={[k.card, { backgroundColor: colors.card, borderColor: colors.line, padding: 0 }]}>
          <View style={[k.tr, k.trHead, { borderBottomColor: colors.line }]}>
            <Text style={[k.th, { color: colors.ink3, flex: 1 }]}>{t('apiKeys.thName')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 90 }]}>{t('apiKeys.thProduct')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 90 }]}>{t('apiKeys.thPrefix')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 90 }]}>{t('common.created')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 90 }]}>{t('apiKeys.thLastUsed')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 80 }]}>{t('common.status')}</Text>
            <Text style={[k.th, { color: colors.ink3, width: 80 }]}> </Text>
          </View>
          {ordered.map(key => (
            <View key={key.id} style={[k.tr, { borderBottomColor: colors.line, opacity: key.revokedAt ? 0.5 : 1 }]}>
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Text style={{ color: colors.ink, fontSize: 13 }}>{key.name}</Text>
                {key.isAdmin && <View style={[k.badge, { backgroundColor: colors.paper2 }]}><Text style={{ color: colors.ink3, fontSize: 10 }}>{t('apiKeys.badgeAdmin')}</Text></View>}
              </View>
              <Text style={{ width: 90, color: colors.ink2, fontSize: 12, fontFamily: font.mono }}>{key.productId}</Text>
              <Text style={{ width: 90, color: colors.ink2, fontSize: 12, fontFamily: font.mono }}>{key.prefix}…</Text>
              <Text style={{ width: 90, color: colors.ink3, fontSize: 12 }}>{relTime(key.createdAt)}</Text>
              <Text style={{ width: 90, color: colors.ink3, fontSize: 12 }}>{key.lastUsedAt ? relTime(key.lastUsedAt) : '—'}</Text>
              <View style={{ width: 80 }}>
                {key.revokedAt
                  ? <View style={[k.badge, { backgroundColor: colors.dangerSoft }]}><Text style={{ color: colors.danger, fontSize: 10 }}>{t('apiKeys.badgeRevoked')}</Text></View>
                  : <View style={[k.badge, { backgroundColor: colors.successSoft }]}><Text style={{ color: colors.success, fontSize: 10 }}>{t('apiKeys.badgeActive')}</Text></View>}
              </View>
              <View style={{ width: 80, alignItems: 'flex-end' }}>
                {!key.revokedAt && <Btn label={t('apiKeys.revoke')} size="sm" onPress={() => revoke(key)} />}
              </View>
            </View>
          ))}
          {keys.length === 0 && <EmptyState title={t('apiKeys.emptyTitle')} body={t('apiKeys.emptyBody')} />}
        </View>
      )}
    </ScrollView>
  );
}

const k = StyleSheet.create({
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  label: { fontSize: 12, marginBottom: 4 },
  input: { height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, fontSize: 13, outlineStyle: 'none' as any },
  tr: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 10, borderBottomWidth: 1 },
  trHead: { paddingVertical: 8 },
  th: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4 },
  badge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 2, alignSelf: 'flex-start' },
});
